package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Nine words in nine scripts, and the only motion on this page that starts by itself.
 *
 * The page argues that a sentence you wrote once should not be billed twelve times. The wall makes
 * that argument before a word of prose does, by simply being the same greeting in nine writing
 * systems at a size where none of them is decoration.
 *
 * **Lines, not words.** Nine words arriving individually is a performance; three lines settling is a
 * page assembling itself. Three lines at [Motion.SettleMs] with a [WALL_STAGGER_MS] offset totals
 * 590ms, and that is the entire self-starting motion budget for the marketing page. Everything else
 * on it moves only because the reader did something.
 */
@Composable
fun ScriptWall() {
    // `role="img"` is what makes the label below reachable. `aria-label` is not honoured on a bare
    // `<div>` — its implicit role is `generic`, which takes no accessible name — and every child
    // here is `aria-hidden`, so the largest object on the page announced nothing at all. `HeroPitch`
    // reasons as though this label works when it explains why its own sentence is the first thing
    // assistive technology meets; it now does. `LocaleStrip` has the same pattern on a `Ul`, where
    // the list role carries it.
    Div(
        ScriptWallStyle.toModifier().toAttrs {
            attr("role", "img")
            attr("aria-label", tr("marketing.script_wall.hello_in_nine_languages", "Hello, in nine languages"))
        },
    ) {
        WALL_LINES.forEachIndexed { index, words ->
            Div(
                ScriptWallLineStyle.toModifier()
                    .styleModifier {
                        // Backwards fill so the line holds its 14px offset through its delay rather
                        // than sitting at rest and then jumping when its turn arrives. Under
                        // `prefers-reduced-motion` the global rule neutralises the delay as well as
                        // the duration, so the hold collapses to nothing and all three lines are
                        // simply where they were always going to be.
                        property("animation-delay", "${index * WALL_STAGGER_MS}ms")
                    }
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                words.forEach { word ->
                    Span(
                        ScriptWallWordStyle.toModifier()
                            .then(if (word.accent) WallAccentStyle.toModifier() else Modifier)
                            .toAttrs {
                                // Load-bearing, not decorative. Han is unified in Unicode, so the
                                // face that draws 你好 is chosen by this attribute and nothing else;
                                // without it the Latin stack renders it with whatever the browser
                                // happens to pick.
                                attr("lang", word.lang)
                            },
                    ) { Text(word.text) }
                }
            }
        }
    }
}

/**
 * One specimen in the wall — a fixed script sample, not copy.
 *
 * **Deliberately not translatable.** Each word is paired with a `lang` chosen to match its writing
 * system, and that attribute is what selects the face: Han is Unicode-unified, so the same
 * codepoints render differently under `zh` and `ja`. A translator editing "Привет" into another
 * language would leave `lang="ru"` behind it and silently break the font selection this wall exists
 * to demonstrate. The band's accessible name is the translatable string here.
 */
private data class WallWord(val text: String, val lang: String, val accent: Boolean = false)

/**
 * Three lines of three. The grouping is deliberate: a reader takes in a line at a glance, and nine
 * separate arrivals would be nine separate demands on their attention.
 *
 * `ru`, `el` and `pt` have no bound face and fall to the Latin stack, which carries Cyrillic and
 * Greek. `zh`, `ar`, `ja`, `hi` and `ko` each resolve to their own Noto face through `:lang()`.
 */
private val WALL_LINES: List<List<WallWord>> = listOf(
    listOf(
        WallWord("Hello", "en", accent = true),
        WallWord("你好", "zh"),
        WallWord("مرحبا", "ar"),
    ),
    listOf(
        WallWord("Привет", "ru"),
        WallWord("こんにちは", "ja"),
        WallWord("Olá", "pt"),
    ),
    listOf(
        WallWord("नमस्ते", "hi"),
        WallWord("안녕", "ko"),
        WallWord("Γεια", "el"),
    ),
)

/**
 * 105ms between line starts, so three 380ms lines finish at 590ms rather than 1,140ms.
 *
 * Sequential lines would read as a queue. Overlapping them by most of their duration reads as one
 * movement with depth to it, and keeps the whole budget under two thirds of a second.
 */
private const val WALL_STAGGER_MS = 105

/**
 * Transform only, and never opacity.
 *
 * A keyframe that starts at `opacity: 0` strands its content invisible in any tab the browser has
 * stopped painting — the animation never runs, the opening frame never advances, and the page is
 * blank for a visitor who switched tabs while it loaded. That has shipped an invisible landing page
 * in this codebase more than once.
 *
 * The rule that prevents it: the base style is always the finished, readable state, and a keyframe
 * may only describe a departure from it. If this animation never runs at all, the wall is exactly
 * where it belongs.
 */
val WallSettle = Keyframes {
    from { Modifier.styleModifier { property("transform", "translateY(14px)") } }
    to { Modifier.styleModifier { property("transform", "none") } }
}

val ScriptWallStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", Space.SM.toString())
        }
}

val ScriptWallLineStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.XXXL)
            .styleModifier {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
            }
            .animation(
                WallSettle.toAnimation(
                    duration = Motion.SettleMs.ms,
                    timingFunction = Motion.EaseEmphasizedTiming,
                    fillMode = AnimationFillMode.Backwards,
                ),
            )
    }
}

val ScriptWallWordStyle = CssStyle {
    base {
        Modifier
            .displayFont()
            .fontSize(TypeScale.Wall)
            .fontWeight(700)
            .lineHeight(1.12)
            // Negative tracking at 88px only. The wall is the one place in the product where type is
            // large enough that default spacing reads as loose; every other heading leaves it alone.
            .letterSpacing((-3.2).px)
            .margin(0.px)
            .color(colorMode.toSitePalette().textPrimary)
    }
    // A phone cannot hold three words of this size on one line, and shrinking the wall to fit is
    // the wrong trade — it stops being a wall. It wraps, and the lines simply grow.
    Breakpoint.ZERO { Modifier.fontSize(40.px).letterSpacing((-1.4).px) }
    Breakpoint.MD { Modifier.fontSize(64.px).letterSpacing((-2.2).px) }
    Breakpoint.LG { Modifier.fontSize(TypeScale.Wall).letterSpacing((-3.2).px) }
}

/** One word carries the accent, so the wall reads as a sentence in nine scripts rather than a swatch. */
val WallAccentStyle = CssStyle.base {
    Modifier.color(colorMode.toSitePalette().accent)
}
